// Escaneo de código de candado (F4 #54). Ayuda de captura para no teclear con guantes en campo:
// el técnico apunta la cámara al QR / código de barras del candado y el wizard rellena la selección.
// Es de SOLO LECTURA (no genera mutación ni sincroniza). Aquí viven las piezas puras y testables; la
// cámara y el bucle de detección están en el componente BarcodeScanner (dependen de APIs del DOM).

import type { Candado } from "@/lib/candados";

/**
 * normalizaCodigo canoniza un código —escaneado o de catálogo— para compararlos sin ruido: recorta
 * espacios y pasa a mayúsculas. Un QR/código impreso puede traer espacios o venir en minúsculas; el
 * `codigo_candado` del catálogo es la referencia de negocio.
 */
export function normalizaCodigo(codigo: string): string {
  return codigo.trim().toUpperCase();
}

/**
 * matchCandadoPorCodigo busca en una lista el candado cuyo `codigo_candado` coincide (canonizado)
 * con el texto escaneado. Devuelve undefined si no hay coincidencia: código de otro candado, ilegible,
 * o de uno que no está en la lista (p. ej. no libre). No decide disponibilidad —el caller pasa ya la
 * lista de candados válidos (libres y activos)—, solo empareja el código.
 */
export function matchCandadoPorCodigo(candados: Candado[], escaneado: string): Candado | undefined {
  const objetivo = normalizaCodigo(escaneado);
  if (objetivo.length === 0) {
    return undefined;
  }
  return candados.find((c) => normalizaCodigo(c.codigo_candado) === objetivo);
}

/**
 * FORMATOS_CODIGO son los formatos que intenta leer el escáner. Cubre QR (candados con etiqueta QR)
 * y los códigos de barras 1D habituales en etiquetas de candado (Code 128/39, EAN, Codabar) más
 * Data Matrix. Los usan tanto BarcodeDetector nativo como el fallback @zxing/browser.
 */
export const FORMATOS_CODIGO = [
  "qr_code",
  "code_128",
  "code_39",
  "ean_13",
  "ean_8",
  "codabar",
  "data_matrix",
] as const;

/**
 * soportaBarcodeDetectorNativo indica si el navegador trae la API nativa BarcodeDetector (Chromium
 * en Android: la ruta rápida y sin dependencias de las tablets de campo). Si no, el escáner recae en
 * @zxing/browser, cargado bajo demanda.
 */
export function soportaBarcodeDetectorNativo(): boolean {
  return typeof window !== "undefined" && "BarcodeDetector" in window;
}
